import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MapPin, ArrowLeft, Check } from "lucide-react";

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [note, setNote] = useState("");
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => navigate("/jobs"));
  }, [id, navigate]);

  const apply = async () => {
    if (!user) { toast.info("Please sign in to apply."); navigate("/login"); return; }
    setApplying(true);
    try {
      await api.post("/applications", { job_id: id, cover_note: note });
      setApplied(true);
      toast.success("Application submitted — good luck!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setApplying(false); }
  };

  if (!job) return <div className="min-h-screen pt-[72px] grid place-items-center text-slateink" data-testid="job-loading">Loading…</div>;

  return (
    <div className="bg-cream min-h-screen pt-[72px]" data-testid="job-detail-page">
      <div className="max-w-[1000px] mx-auto px-6 lg:px-10 pt-12 lg:pt-16 pb-28">
        <Link to="/jobs" data-testid="job-back" className="inline-flex items-center gap-2 text-slateink hover:text-navy transition-colors mb-10">
          <ArrowLeft size={16} /> All opportunities
        </Link>

        <div className="grid lg:grid-cols-[1fr_320px] gap-12">
          <div>
            <span className="font-mono-ui text-[0.6rem] uppercase tracking-widest text-cobalt border border-cobalt/30 px-2 py-1">{job.category}</span>
            <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-navy mt-6 leading-tight">{job.title}</h1>
            <p className="text-xl text-slateink mt-2">{job.company}</p>

            <div className="flex flex-wrap items-center gap-5 mt-6 text-navy/80">
              <span className="flex items-center gap-1.5"><MapPin size={16} />{job.location} · {job.location_type}</span>
              <span className="font-medium">{job.salary}</span>
              <span className="font-mono-ui text-xs uppercase tracking-widest border border-border px-2 py-1">{job.level}</span>
            </div>

            <div className="h-px bg-border my-10" />

            <h2 className="font-display text-2xl font-semibold text-navy mb-4">About the role</h2>
            <p className="text-slateink text-lg leading-relaxed">{job.summary}</p>

            <h2 className="font-display text-2xl font-semibold text-navy mt-10 mb-4">What you'll bring</h2>
            <ul className="space-y-3">
              {job.requirements?.map((r, i) => (
                <li key={i} className="flex items-start gap-3 text-slateink text-lg">
                  <Check size={20} className="text-cobalt mt-1 shrink-0" />{r}
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-2 mt-10">
              {job.tags?.map((t) => (
                <span key={t} className="font-mono-ui text-[0.65rem] uppercase tracking-widest bg-secondary text-navy px-3 py-1.5">{t}</span>
              ))}
            </div>
          </div>

          {/* Apply card */}
          <div className="lg:sticky lg:top-24 h-fit">
            <div className="bg-white border border-navy p-6">
              <p className="font-display text-2xl font-semibold text-navy">Apply now</p>
              <p className="text-sm text-slateink mt-1 mb-5">Joy clients get fast-tracked to hiring teams.</p>
              {applied ? (
                <div className="bg-secondary p-5 text-center" data-testid="job-applied-state">
                  <Check size={28} className="text-cobalt mx-auto" />
                  <p className="font-medium text-navy mt-2">Application submitted</p>
                  <Link to="/dashboard" className="text-cobalt text-sm hover:underline">View in dashboard</Link>
                </div>
              ) : (
                <>
                  <textarea data-testid="job-cover-note" value={note} onChange={(e) => setNote(e.target.value)} rows={4}
                    placeholder="Add a short note (optional)…"
                    className="w-full border border-border p-3 outline-none focus:border-cobalt resize-none text-sm text-navy mb-4" />
                  <button data-testid="job-apply-button" onClick={apply} disabled={applying}
                    className="w-full px-6 py-3.5 rounded-full bg-navy text-white font-medium hover:bg-cobalt transition-colors disabled:opacity-60">
                    {applying ? "Submitting…" : user ? "Submit application" : "Sign in to apply"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
